import React, { useMemo } from "react";
import {
  createBrowserRouter,
  type RouteObject,
  RouterProvider,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import AppLayout from "@/components/AppLayout";
import ErrorPage from "@/pages/ErrorPage";
import Root from "@/pages/Root";
import type { Role } from "@/routerConfig";
import navRouteTree from "@/routerConfig/navRouteTree";
import { getChildrenForRoutes } from "@/routerConfig/routeBuilder";
import { filterExcludedNavItems } from "@/routerConfig/router.helpers";
import { useAppSelector } from "@/storeHooks";

// Call this AFTER Sentry.init()
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const createRoutesConfig = (userRole: Role[]): RouteObject[] => {
  const menuItems = filterExcludedNavItems(navRouteTree, userRole);
  return [
    {
      path: "/",
      element: (
        <AppLayout menuItems={menuItems}>
          <Root />
        </AppLayout>
      ),
      errorElement: (
        <AppLayout menuItems={menuItems}>
          <ErrorPage />
        </AppLayout>
      ),
      children: getChildrenForRoutes(userRole),
    },
  ];
};

const LazyRouterProvider = () => {
  const { roles: userRole } = useAppSelector((state) => state.user);
  const basename = String(import.meta.env.VITE_ROUTER_BASE ?? "");
  const router = useMemo(
    () => sentryCreateBrowserRouter(createRoutesConfig(userRole), { basename }),
    [basename, userRole]
  );

  return <RouterProvider router={router} />;
};

export default LazyRouterProvider;
